import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Per-user CTM sync state (public.ctm_user_links), kept by the ctm-sync-user
 * reconciler for tenants whose CTM is Steddy-provisioned. RLS: a user sees
 * their own row; admins see the whole company.
 */
export type CtmUserLinkStatus = 'pending' | 'running' | 'synced' | 'removed' | 'skipped' | 'error';

export interface CtmUserLink {
  user_id: string;
  status: CtmUserLinkStatus;
  last_error: string | null;
  attempts: number;
  synced_at: string | null;
  updated_at: string;
}

export const CTM_USER_LINKS_KEY = ['ctm-user-links'] as const;

/** Map of user id → link. Realtime invalidates it; while anything is pending it also polls. */
export function useCtmUserLinks(enabled = true) {
  return useQuery({
    queryKey: CTM_USER_LINKS_KEY,
    enabled,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const map = query.state.data as Map<string, CtmUserLink> | undefined;
      if (!map) return false;
      for (const link of map.values()) {
        if (link.status === 'pending' || link.status === 'running') return 10_000;
      }
      return false;
    },
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ctm_user_links')
        .select('user_id, status, last_error, attempts, synced_at, updated_at');
      if (error) throw error;
      const map = new Map<string, CtmUserLink>();
      for (const row of data ?? []) map.set(row.user_id, row as CtmUserLink);
      return map;
    },
  });
}

interface RetryResult {
  status: CtmUserLinkStatus | 'deferred';
  last_error: string | null;
}

/** Admin-only: run the CTM sync for one user now and report what happened. */
export function useRetryCtmUserSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }): Promise<RetryResult> => {
      // Raw fetch rather than supabase.functions.invoke so the error body is
      // readable when the function answers 4xx/5xx (invoke swallows it).
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const res = await fetch(`${SUPABASE_URL}/functions/v1/ctm-sync-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${accessToken ?? SUPABASE_KEY}`,
        },
        body: JSON.stringify({ action: 'retry', user_id: userId }),
      });

      const raw = await res.text();
      let parsed: { status?: RetryResult['status']; last_error?: string | null; error?: string } | null = null;
      try { parsed = raw ? JSON.parse(raw) : null; } catch { /* non-JSON body */ }

      if (!res.ok) throw new Error(parsed?.error || raw || `Edge function returned ${res.status}`);
      if (parsed?.error) throw new Error(parsed.error);
      return { status: parsed?.status ?? 'pending', last_error: parsed?.last_error ?? null };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: CTM_USER_LINKS_KEY });
      switch (result.status) {
        case 'synced':
          toast.success('CTM agent is in place');
          break;
        case 'removed':
          toast.success('CTM agent removed');
          break;
        case 'skipped':
          toast('Nothing to sync: CTM user sync does not apply to this workspace.');
          break;
        case 'error':
          toast.error(result.last_error || 'CTM refused the change');
          break;
        default:
          toast(result.last_error ? `Will retry: ${result.last_error}` : 'Queued; CTM will be updated shortly.');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not sync with CTM');
    },
  });
}
